import React from 'react';
import { StaticImage } from 'gatsby-plugin-image';

import { type StepsPopProps } from '../StepsPop';
import { type StepProps } from '../components/Step';
import { LOCAL_COLORS } from 'styles/constants';

const disclaimer = '*Subject to consultation. Cancel anytime.';

const sharedSteps: StepProps[] = [
  {
    header: 'Connect with your provider.',
    body: 'Once one of our Licensed Dermatology Providers reviews your "skinfo", they can help create a treatment plan for you.',
    desktopImage: (
      <StaticImage
        src="../images/doctorDesktop@2x.jpg"
        alt="Blonde woman in a white lab coat and beige turtleneck, smiling, sitting behind a desk on a leather chair with a computer."
        loading="eager"
        width={1000}
        objectFit="cover"
        objectPosition="center"
      />
    ),
    mobileImage: (
      <StaticImage
        src="../images/doctorDesktop@2x.jpg"
        alt="Blonde woman in a white lab coat and beige turtleneck, smiling, sitting behind a desk on a leather chair with a computer."
        loading="eager"
        objectFit="fill"
        objectPosition="center"
      />
    ),
  },
  {
    header: 'We customize your plan.',
    body: 'Your provider prescribes one of our personalized formulas based on a variety of factors specific to you, then selects 3 clinically proven ingredients that work better together, to give you more effective results.',

    desktopImage: (
      <StaticImage
        src="../images/bottleDesktop@2x.jpg"
        alt="Two hands, the left hand holding a white Curology Custom Formula Rx bottle, the right hand holding up a finger with a pea-size amount of the product."
        loading="eager"
        width={1000}
        objectFit="cover"
        objectPosition="center"
      />
    ),
    mobileImage: (
      <StaticImage
        src="../images/bottleDesktop@2x.jpg"
        alt="Two hands, the left hand holding a white Curology Custom Formula Rx bottle, the right hand holding up a finger with a pea-size amount of the product."
        loading="eager"
        objectFit="fill"
        objectPosition="center"
      />
    ),
  },
  {
    header: 'Receive your skincare.',
    body: 'Once your provider has prescribed your formula and created your personalized plan, and you’ve selected your routine add-ons, your products will be shipped directly to your door every 2 months!',

    desktopImage: (
      <StaticImage
        src="../images/packageDesktop@1x.jpg"
        alt="Purple Curology Cleanser tube, white Curology Custom Formula Rx bottle, and purple Curology Moisturizer bottle standing to the right of a square brown Curology packaging box on a light purple background."
        width={1000}
        objectFit="cover"
        objectPosition="center"
      />
    ),
    mobileImage: (
      <StaticImage
        src="../images/packageDesktop@1x.jpg"
        alt="Purple Curology Cleanser tube, white Curology Custom Formula Rx bottle, and purple Curology Moisturizer bottle standing to the right of a square brown Curology packaging box on a light purple background."
        loading="eager"
        objectFit="fill"
        objectPosition="center"
      />
    ),
  },
];

export const POP_REDESIGN_STEPS_CONTENT: StepsPopProps = {
  header: 'Better skin in just four simple steps',
  subheader:
    'Our Licensed Dermatology Providers are here for you every step of the way.',
  steps: [
    {
      header: 'Tell us about you.',
      body: 'You’ll start with our quick-yet-comprehensive consultation: share medical history, and lifestyle factors, along with current skin concerns, goals, and a few photos for our medical team to review. These photos also help us track your amazing progress!',
      desktopImage: (
        <StaticImage
          src="../images/brunetteDesktop@1x.jpg"
          alt="Brunette woman with long hair, facing to the side, face turned towards the camera."
          loading="eager"
          width={1000}
          objectFit="cover"
          objectPosition="center"
        />
      ),
      mobileImage: (
        <StaticImage
          src="../images/brunetteMobile.jpg"
          alt="Brunette woman with long hair, facing to the side, face turned towards the camera."
          loading="eager"
          objectFit="fill"
          objectPosition="center"
        />
      ),
    },
    ...sharedSteps,
  ],
  ctaButton: {
    buttonText: 'Find my personalized formula',
    color: 'primary',
  },
  disclaimerText: disclaimer,
  backgroundColor: LOCAL_COLORS.surfaceBrandBolder,
};

export const ACNE_STEPS_CONTENT: StepsPopProps = {
  header: 'Clearer skin in just four simple steps',
  subheader:
    'Our Licensed Dermatology Providers are here for you every step of the way.',
  steps: [
    {
      header: 'Tell us about you.',
      body: 'You’ll start with our quick-yet-comprehensive consultation: share medical history, and lifestyle factors, along with current skin concerns, goals, and a few photos for our medical team to review. These photos also help us track your amazing progress!',
      desktopImage: (
        <StaticImage
          src="../images/acneDesktop@1x.jpg"
          alt="Brunette woman with curly hair and nose ring, facing to the left, before Curology use."
          loading="eager"
          width={1000}
          objectFit="cover"
          objectPosition="center"
        />
      ),
      mobileImage: (
        <StaticImage
          src="../images/acneMobile@1x.jpg"
          alt="Brunette woman with curly hair and nose ring, facing to the left, before Curology use."
          loading="eager"
          objectFit="fill"
          objectPosition="center"
        />
      ),
    },
    sharedSteps[0],
    {
      ...sharedSteps[1],
      body: 'Your provider prescribes one of our personalized formulas based on a variety of factors specific to you. Each formula contains 3 clinically proven ingredients that work better together, to give you more effective results.',
    },
    {
      ...sharedSteps[2],
      body: 'Once your provider has prescribed your formula, and you’ve selected your routine add-ons, your products will be shipped directly to your door!',
    },
  ],
  ctaButton: {
    buttonText: 'Find my personalized formula',
  },
  disclaimerText: disclaimer,
};

export const CHIA_STEPS_CONTENT: StepsPopProps = {
  header: 'How it works',
  backgroundColor: '#e2e0d3',
  steps: [
    POP_REDESIGN_STEPS_CONTENT.steps[0],
    sharedSteps[0],
    sharedSteps[1],
    {
      ...sharedSteps[2],
      desktopImage: (
        <StaticImage
          src="../images/chiaPackage@2x.jpg"
          alt="Brunette woman with long hair, facing to the side, face turned towards the camera."
          loading="eager"
          width={1000}
          objectFit="cover"
          objectPosition="center"
        />
      ),
      mobileImage: (
        <StaticImage
          src="../images/chiaPackage@2x.jpg"
          alt="Brunette woman with long hair, facing to the side, face turned towards the camera."
          loading="eager"
          objectFit="fill"
          objectPosition="center"
        />
      ),
    },
  ],
  ctaButton: {
    buttonText: 'Find your personalized formula',
  },
  disclaimerText: disclaimer,
};

export const RETAIL_OFFER_STEPS_CONTENT: StepsPopProps = {
  ...POP_REDESIGN_STEPS_CONTENT,
  backgroundColor: LOCAL_COLORS.surfaceWhite,
  ctaButton: {
    buttonText: 'Find your personalized formula',
    color: 'primary',
  },
};
